import type {Customer} from "@/components/customers/scripts/types";
import {
    addressHasChanges,
    createNewAddress,
    createEmptyBankAccount,
    createEmptyLogo,
    createNewPerson, personHasChanges,
    resetAddressObject,
    resetBankAccountObject,
    resetPersonObject,
    bankAccountHasChanges, logoHasChanges, resetLogoObject
} from "@/scripts/Common/helpers";

import {
  CustomerState,
} from "@/Enums";

import {
  areNumberArraysEqual,
} from "@/scripts/Common/helpers/arrayHelpers";

import {
  bankTransferDetailsHaveChanges,
  createEmptyBankTransferDetails,
  resetBankTransferDetails,
} from "@/components/children/bankTransferDetails/scripts";

const createEmptyCustomer = () => {
    const customer: Customer = {
        id: 0,
        companyName: '',
        contactEmployeeId: 0,
        contactEmployee: createNewPerson(),
        taxNumber: undefined,
        vatIdentificationNumber: undefined,
        contactAddressId: 0,
        contactAddress: createNewAddress(),
        businessAddressId: 0,
        businessAddress: createNewAddress(),
        pointOfSalesCount: undefined,
        status: CustomerState.Interested,
        interestedNumber: undefined,
        provision: undefined,
        businessBankAccountId: 0,
        businessBankAccount: createEmptyBankAccount(),
        creditorId: undefined,
        accountingPartnerId: undefined,
        isWebsitePartner: false,
        websiteUrl: '',
        websiteLogo: createEmptyLogo(),
        useVoucher: false,
        voucherAmount: 0,
        useEcTerminal: false,
        hardware: '',
        isInternetAvailable: false,
        deploymentDate: undefined,
        isSparkassenKunde: false,
        category: '',
        isEmployer: false,
        serviceIds: [],
        bankAccountCreditorId: undefined,
        contractStart: undefined,
        contractEnd: undefined,
        bankTransferDetailsId: 0,
        bankTransferDetails: createEmptyBankTransferDetails()
    }

    return customer;
}

const resetCustomerWebsiteLogo = (originalObject: Customer, changedObject: Customer) => {
  if(!originalObject.websiteLogo || !changedObject.websiteLogo){
    changedObject.websiteLogo = originalObject.websiteLogo;
    return;
  }

  resetLogoObject(originalObject.websiteLogo, changedObject.websiteLogo);
}

const resetCustomerObject = (originalObject: Customer, changedObject: Customer) => {
    changedObject.id = originalObject.id;
    changedObject.companyName = originalObject.companyName;
    changedObject.contactEmployeeId = originalObject.contactEmployeeId;
    resetPersonObject(originalObject.contactEmployee, changedObject.contactEmployee)
    changedObject.taxNumber = originalObject.taxNumber;
    changedObject.vatIdentificationNumber = originalObject.vatIdentificationNumber;
    changedObject.contactAddressId = originalObject.contactAddressId;
    resetAddressObject(originalObject.contactAddress, changedObject.contactAddress)
    changedObject.businessAddressId = originalObject.businessAddressId;
    resetAddressObject(originalObject.businessAddress, changedObject.businessAddress)
    changedObject.pointOfSalesCount = originalObject.pointOfSalesCount;
    changedObject.status = originalObject.status;
    changedObject.interestedNumber = originalObject.interestedNumber;
    changedObject.provision = originalObject.provision;
    changedObject.businessBankAccountId = originalObject.businessBankAccountId;
    changedObject.creditorId = originalObject.creditorId;
    changedObject.accountingPartnerId = originalObject.accountingPartnerId;
    changedObject.isWebsitePartner = originalObject.isWebsitePartner;
    changedObject.websiteUrl = originalObject.websiteUrl;
    changedObject.useVoucher = originalObject.useVoucher;
    changedObject.voucherAmount = originalObject.voucherAmount;
    changedObject.useEcTerminal = originalObject.useEcTerminal;
    changedObject.hardware = originalObject.hardware;
    changedObject.isInternetAvailable = originalObject.isInternetAvailable;
    changedObject.deploymentDate = originalObject.deploymentDate;
    changedObject.isSparkassenKunde = originalObject.isSparkassenKunde;
    changedObject.category = originalObject.category;
    changedObject.isEmployer = originalObject.isEmployer;
    changedObject.serviceIds = originalObject.serviceIds;
    changedObject.bankAccountCreditorId = originalObject.bankAccountCreditorId;
    changedObject.contractStart = originalObject.contractStart;
    changedObject.contractEnd = originalObject.contractEnd;
    resetCustomerWebsiteLogo(originalObject, changedObject);
    changedObject.bankTransferDetailsId = originalObject.bankTransferDetailsId;
    resetBankTransferDetails(originalObject.bankTransferDetails, changedObject.bankTransferDetails);

    if(!originalObject.businessBankAccount){
        changedObject.businessBankAccount = undefined;
    }
    if(!changedObject.businessBankAccount){
        changedObject.businessBankAccount = createEmptyBankAccount();
    }
    if(changedObject.businessBankAccount && originalObject.businessBankAccount){
        resetBankAccountObject(originalObject.businessBankAccount, changedObject.businessBankAccount);
    }
}

const customerHasChanges = (originalObject: Customer, changedObject: Customer) => {
    return changedObject.id !== originalObject.id
        || changedObject.companyName !== originalObject.companyName
        || changedObject.contactEmployeeId !== originalObject.contactEmployeeId
        || personHasChanges(originalObject.contactEmployee, changedObject.contactEmployee)
        || changedObject.taxNumber !== originalObject.taxNumber
        || changedObject.vatIdentificationNumber !== originalObject.vatIdentificationNumber
        || changedObject.contactAddressId !== originalObject.contactAddressId
        || addressHasChanges(originalObject.contactAddress, changedObject.contactAddress)
        || changedObject.businessAddressId !== originalObject.businessAddressId
        || addressHasChanges(originalObject.businessAddress, changedObject.businessAddress)
        || changedObject.pointOfSalesCount !== originalObject.pointOfSalesCount
        || changedObject.status !== originalObject.status
        || changedObject.interestedNumber !== originalObject.interestedNumber
        || changedObject.provision?.toString(10) !== originalObject.provision?.toString(10)
        || changedObject.businessBankAccountId !== originalObject.businessBankAccountId
        || bankAccountHasChanges(originalObject.businessBankAccount, changedObject.businessBankAccount)
        || changedObject.creditorId !== originalObject.creditorId
        || changedObject.accountingPartnerId !== originalObject.accountingPartnerId
        || changedObject.isWebsitePartner !== originalObject.isWebsitePartner
        || changedObject.websiteUrl !== originalObject.websiteUrl
        || logoHasChanges(originalObject.websiteLogo, changedObject.websiteLogo)
        || changedObject.useVoucher !== originalObject.useVoucher
        || changedObject.voucherAmount !== originalObject.voucherAmount
        || changedObject.useEcTerminal !== originalObject.useEcTerminal
        || changedObject.hardware !== originalObject.hardware
        || changedObject.isInternetAvailable !== originalObject.isInternetAvailable
        || changedObject.deploymentDate !== originalObject.deploymentDate
        || changedObject.isSparkassenKunde !== originalObject.isSparkassenKunde
        || changedObject.category !== originalObject.category
        || changedObject.isEmployer !== originalObject.isEmployer
        || !areNumberArraysEqual(originalObject.serviceIds, changedObject.serviceIds)
        || changedObject.bankAccountCreditorId !== originalObject.bankAccountCreditorId
        || changedObject.contractStart !== originalObject.contractStart
        || changedObject.contractEnd !== originalObject.contractEnd
        || changedObject.bankTransferDetailsId !== originalObject.bankTransferDetailsId
        || bankTransferDetailsHaveChanges(originalObject.bankTransferDetails, changedObject.bankTransferDetails);
}

export {
    createEmptyCustomer,
    resetCustomerObject,
    bankAccountHasChanges,
    customerHasChanges,
}
